import { useCallback, useEffect, useState } from "react";

import type { GetMenuAnchor, GetMenuBoundary, MenuLayout } from "../types";

export const MATCH_MENU_GAP_PX = 4;

export function getMenuLayout(
  container: HTMLElement | null,
  getMenuAnchor?: GetMenuAnchor,
  getMenuBoundary?: GetMenuBoundary,
): MenuLayout | null {
  if (!container) return null;

  const control = container.querySelector<HTMLElement>(".cb-select__control");
  const target = getMenuAnchor ? getMenuAnchor(container) : control ?? container;

  const { width, left, right, bottom } = target.getBoundingClientRect();
  const boundaryRight = getMenuBoundary?.(container) ?? undefined;

  return { width, left, right, top: bottom - MATCH_MENU_GAP_PX, boundaryRight };
}

type UseMenuPortalLayoutArgs = {
  containerRef: React.RefObject<HTMLDivElement | null>;
  menuIsOpen: boolean;
  menuFitContent: boolean;
  getMenuAnchor?: GetMenuAnchor;
  getMenuBoundary?: GetMenuBoundary;
};

export function useMenuPortalLayout({
  containerRef,
  menuIsOpen,
  menuFitContent,
  getMenuAnchor,
  getMenuBoundary,
}: UseMenuPortalLayoutArgs) {
  const [menuLayoutTick, setMenuLayoutTick] = useState(0);

  const bumpMenuLayout = useCallback(() => {
    setMenuLayoutTick((tick) => tick + 1);
  }, []);

  useEffect(() => {
    if (!menuFitContent || !menuIsOpen) return undefined;

    window.addEventListener("resize", bumpMenuLayout);
    window.addEventListener("scroll", bumpMenuLayout, true);

    return () => {
      window.removeEventListener("resize", bumpMenuLayout);
      window.removeEventListener("scroll", bumpMenuLayout, true);
    };
  }, [menuFitContent, menuIsOpen, bumpMenuLayout]);

  const menuLayout =
    menuFitContent && menuLayoutTick >= 0
      ? getMenuLayout(containerRef.current, getMenuAnchor, getMenuBoundary)
      : null;

  return {
    menuLayout,
    bumpMenuLayout,
    needsMenuLayout: menuFitContent,
  };
}
